/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import CatalogTarifsContext from './CatalogTarifsContext';
import initialCatalogTarifsState from './initialCatalogTarifsState';
import catalogTarifsReducer from 'contexts/catalogTarifs/reducer/catalogTarifs.reducer';
import * as action from 'contexts/catalogTarifs/reducer/catalogTarifs.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';
import { useTranslation } from 'react-i18next';
import {
  IcreateTarifAPIRequestBody,
  IgetCatalogTarifsAPIResponseDataItem,
  IupdateTarifAPIRequestBody,
} from 'api/endpoints/catalogTarifs/catalogTarifs.response.interface';
import { IdownloadTarifActionProps } from 'interfaces/catalogTarifs.interface';
import * as types from './reducer/catalogTarifs.types';

function CatalogTarifsProvider(props: IChildrenProps) {
  const [catalogTarifsState, dispatch] = useReducer(
    catalogTarifsReducer,
    initialCatalogTarifsState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();

  const getCatalogTarifs = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    return await action.getCatalogTarifsAction({
      dispatch,
      tenantId: company?.id,
      token,
      translate,
    });
  }, [company?.id]);

  const resetCatalogTarifs = useCallback(async () => {
    dispatch({ type: types.RESET_CATALOG_TARIFS });
  }, []);

  const selectTarif = useCallback(
    async (tarif: IgetCatalogTarifsAPIResponseDataItem | null) => {
      return await action.selectTarifAction({
        dispatch,
        tarif,
      });
    },
    []
  );

  const createTarif = useCallback(
    async ({
      body,
      clients,
    }: {
      body: IcreateTarifAPIRequestBody;
      clients: Array<string>;
    }) => {
      const token = await getAccessTokenSilently();
      if (!token || !company?.id) return false;
      return await action.createTarifAction({
        dispatch,
        tenantId: company.id,
        body,
        clients,
        tarifs: catalogTarifsState.catalogTarifs,
        token,
        translate,
      });
    },
    [catalogTarifsState.catalogTarifs, company?.id]
  );

  const updateTarif = useCallback(
    async ({
      body,
      clients,
    }: {
      body: IupdateTarifAPIRequestBody;
      clients: Array<string>;
    }) => {
      const token = await getAccessTokenSilently();
      if (!token || !company?.id) return false;
      return await action.updateTarifAction({
        dispatch,
        tenantId: company.id,
        body,
        clients,
        tarifs: catalogTarifsState.catalogTarifs,
        token,
        translate,
      });
    },
    [company?.id, catalogTarifsState.catalogTarifs]
  );

  const deleteTarif = useCallback(
    async (tarifId: string) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.deleteTarifAction({
        dispatch,
        tarifId,
        tarifs: catalogTarifsState.catalogTarifs,
        token,
        translate,
      });
    },
    [catalogTarifsState.catalogTarifs]
  );

  const downloadTarif = useCallback(
    async ({ tarifId, tarifCode, pendingProducts }: IdownloadTarifActionProps) => {
      const token = await getAccessTokenSilently();
      if (!token || !company?.id) return false;
      return await action.downloadTarifAction({
        dispatch,
        tenantId: company.id,
        tarifId,
        tarifCode,
        pendingProducts,
        token,
        translate,
      });
    },
    [company?.id]
  );

  const memoProvider = useMemo(
    () => ({
      ...catalogTarifsState,
      getCatalogTarifs,
      resetCatalogTarifs,
      selectTarif,
      createTarif,
      updateTarif,
      deleteTarif,
      downloadTarif,
    }),
    [
      catalogTarifsState,
      getCatalogTarifs,
      resetCatalogTarifs,
      selectTarif,
      createTarif,
      updateTarif,
      deleteTarif,
      downloadTarif,
    ]
  );

  return (
    <CatalogTarifsContext.Provider value={memoProvider}>
      {props.children}
    </CatalogTarifsContext.Provider>
  );
}

export default memo(CatalogTarifsProvider);
